import { useInView } from "@/hooks/useInView";
import { Waves, CalendarClock, MessageCircleHeart } from "lucide-react";

const MY_SPACE_URL = `${process.env.PUBLIC_URL}/screenshots/myspace-1.webp`;

const PILLARS = [
  {
    icon: Waves,
    title: "Regulate at your pace",
    description:
      "Four fidget tools, breathing exercises, and grounding techniques — all sensory-safe, all self-paced.",
    color: "brand-teal",
    bg: "bg-brand-teal/10",
  },
  {
    icon: CalendarClock,
    title: "Structure without rigidity",
    description:
      "Visual routines, gentle reminders, and daily check-ins that bring predictability to your day.",
    color: "brand-purple",
    bg: "bg-brand-purple/10",
  },
  {
    icon: MessageCircleHeart,
    title: "Communicate your way",
    description:
      "Quick-tap communication cards, sensory-friendly places, and a community that understands.",
    color: "brand-peach",
    bg: "bg-brand-peach/20",
  },
];

export default function AboutSection() {
  const [ref, inView] = useInView();
  const [cardsRef, cardsInView] = useInView();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-20 md:py-32 px-6 md:px-12 lg:px-20 relative"
    >
      <div ref={ref} className="max-w-7xl mx-auto">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20 transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div>
            <p className="text-sm uppercase tracking-widest text-brand-purple font-semibold mb-4">
              About NeuroNest
            </p>
            <h2
              data-testid="about-headline"
              className="font-heading text-3xl md:text-5xl font-bold text-[#2F3A56] leading-tight mb-6"
            >
              A companion, not a treatment.
            </h2>
            <p className="text-base md:text-lg text-[#5A6376] leading-relaxed mb-4">
              NeuroNest is built on one belief: neurodivergent people deserve
              tools designed <em>for</em> them — not as an afterthought.
            </p>
            <p className="text-base md:text-lg text-[#5A6376] leading-relaxed">
              Whether you experience sensory sensitivities, need routine support,
              or want calming tools for overwhelming moments, NeuroNest meets you
              where you are. No fixing. No tracking. No guilt.
            </p>
          </div>
          <div className="flex justify-center">
            <div className="relative">
              {/* Family photo behind */}
              <div className="relative w-full max-w-sm">
                <div className="absolute inset-0 bg-brand-purple/10 rounded-[2rem] rotate-2 scale-[1.02]" />
                <img
                  data-testid="about-image"
                  src={`${process.env.PUBLIC_URL}/screenshots/family.webp`}
                  alt="Happy family embracing and smiling together"
                  className="relative rounded-[2rem] w-full h-auto object-cover shadow-[0_8px_40px_rgba(0,0,0,0.06)] bg-[#F0F1F3] min-h-[250px]"
                  loading="lazy"
                />
              </div>
              {/* My Space phone mockup overlapping */}
              <div className="absolute -bottom-8 -right-6 md:-right-16 w-[140px] md:w-[170px]">
                <div className="bg-[#2F3A56] rounded-[24px] p-[5px] shadow-[0_12px_40px_rgba(47,58,86,0.25)]">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[50px] h-[12px] bg-[#2F3A56] rounded-b-lg z-10" />
                  <div className="rounded-[20px] overflow-hidden bg-[#F0F1F3] min-h-[200px]">
                    <img
                      data-testid="about-app-preview"
                      src={MY_SPACE_URL}
                      alt="My Space — Your comfort anchors, fidgets, and sensory mood"
                      className="w-full h-auto block"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-[3px] left-1/2 -translate-x-1/2 w-[50px] h-[2px] bg-white/30 rounded-full" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Three Pillars */}
        <div
          ref={cardsRef}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
        >
          {PILLARS.map((pillar, i) => (
            <div
              key={pillar.title}
              data-testid={`pillar-card-${i}`}
              className={`bg-white rounded-3xl border border-[#E7E9EC] p-8 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.07)] transition-all duration-500 hover:-translate-y-1 ${
                cardsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div
                className={`w-12 h-12 rounded-2xl ${pillar.bg} flex items-center justify-center mb-5`}
              >
                <pillar.icon
                  size={22}
                  className={`text-${pillar.color}`}
                  strokeWidth={1.5}
                />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#2F3A56] mb-3">
                {pillar.title}
              </h3>
              <p className="text-[#5A6376] leading-relaxed text-sm">
                {pillar.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
